import { useEffect, useRef, useState } from "react"
import type { ColorScheme } from "./brand"
import { Canvas } from "./Canvas"
import type { InteractionEvents } from "./events"
import type { FlowgramState } from "./flowgram-state"
import { FlowgramInteractor } from "./FlowgramInteractor"
import { FlowgramMorphRenderer } from "./FlowgramMorphRenderer"
import { FlowgramDrawingRenderer } from "./FlowgramDrawingRenderer"
import { FlowgramSVGRenderer } from "./FlowgramSVGRenderer"

type Props = {
  /**
   * Generator settings
   */
  flowgram: FlowgramState
  renderMode: "morph" | "draw" | "static"
  /**
   * Triggered every frame during a colour transition
   *
   * @param bgBrand Named target BG colour
   * @param fgBrand Named target FG colour
   * @param bgHex Current background hex value
   * @param fgHex Current foreground hex value (unless multi-color is enabled)
   */
  onColorChange?(scheme: ColorScheme): void
  primaryDimension?: "width" | "height"
} & InteractionEvents

/**
 * Component which renders an interactive Flowgram
 */
export function FlowgramDisplay(props: Props) {
  const { settings, processor, shapes, spec } = props.flowgram
  const container = useRef<HTMLDivElement>(null!)
  const [viewportSize, setViewportSize] = useState({
    width: 1000,
    height: 1000,
  })

  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      setViewportSize({
        width: entries[0].contentRect.width,
        height: entries[0].contentRect.height,
      })
    })
    ro.observe(container.current)

    return () => ro.disconnect()
  }, [])

  let targetWidth = 0
  let targetHeight = 0
  const outputAspect = processor.cols / processor.rows
  let primaryDimension = props.primaryDimension
    ? props.primaryDimension
    : outputAspect > viewportSize.width / viewportSize.height
      ? "width"
      : "height"
  if (primaryDimension === "width") {
    targetWidth = viewportSize.width
    targetHeight = targetWidth * outputAspect
  } else {
    targetHeight = viewportSize.height
    targetWidth = targetHeight * outputAspect
  }
  // console.log({ targetWidth, targetHeight });

  if (settings.renderMode === "static") {
    // targetWidth = settings.exportWidth
    // targetHeight = (processor.rows / processor.cols) * targetWidth
  }

  const cellSize = targetWidth / processor.cols // Size of each cell in pixels

  return (
    <div ref={container} className="absolute inset-0 flex flex-col items-center justify-center overflow-hidden">
      <div
        className="relative flex-none"
        style={{
          aspectRatio: processor.cols + " / " + processor.rows,
          // width: "100%",
          // height: "auto",
          width: primaryDimension === "width" ? "100%" : "auto",
          height: primaryDimension === "height" ? "100%" : "auto",
        }}
      >
        {/* Debugging canvas */}
        {settings.debugCells && (
          <div className="absolute inset-0 pointer-events-none">
            <Canvas
              draw={(ctx) => {
                const rows = processor.rows
                const cols = processor.cols
                ctx.fillStyle = "rgba(0,0,0,0.5)"
                ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height)
                const padding = settings.exportPadding * cols * cellSize
                ctx.canvas.width = cols * cellSize + padding * 2
                ctx.canvas.height = rows * cellSize + padding * 2
                ctx.translate(padding, padding)

                if (settings.debugCells) {
                  for (const cell of processor.grid) {
                    ctx.strokeStyle = "black"
                    ctx.fillStyle =
                      cell.layerId === 0 ? "#000000" : `hsl(${(cell.layerId * 5123.523414) % 360}, 100%, 50%)`
                    ctx.globalAlpha = cell.isEdge ? 0.4 : 0.1
                    ctx.fillRect(cell.x * cellSize, cell.y * cellSize, cellSize, cellSize)
                    ctx.strokeRect(cell.x * cellSize, cell.y * cellSize, cellSize, cellSize)
                    ctx.fillStyle = "#000000"
                    ctx.textBaseline = "top"
                    ctx.textAlign = "center"
                    if (cell.noGrow) {
                      ctx.globalAlpha = 1
                      ctx.strokeStyle = "red"
                      ctx.lineWidth = 1
                      ctx.beginPath()
                      ctx.moveTo(cell.x * cellSize, cell.y * cellSize)
                      ctx.lineTo(cell.x * cellSize + cellSize, cell.y * cellSize + cellSize)
                      ctx.stroke()
                    }
                  }

                  shapes.forEach((shape, i) => {
                    const p = shape.tracedPath
                    for (let i = 0; i < p.length; i++) {
                      const cell = p[i]
                      ctx.strokeStyle = "black"
                      ctx.fillStyle = "#000000"
                      ctx.textBaseline = "middle"
                      ctx.globalAlpha = 1
                      ctx.textAlign = "center"
                      ctx.fillText(
                        (cell.corner
                          ? cell.corner + "," + [cell.radius].join(",")
                          : cell.vertical
                            ? "V"
                            : cell.horizontal
                              ? "H"
                              : cell.dx + "," + cell.dy
                        ).toUpperCase(),
                        cell.x * cellSize + cellSize / 2,
                        cell.y * cellSize + cellSize / 2,
                      )
                    }
                  })
                }

                if (settings.debugShapes) {
                  ctx.font = `${targetWidth / settings.cols} Arial`
                  ctx.strokeStyle = "black"
                  ctx.textBaseline = "middle"
                  ctx.globalAlpha = 1
                  ctx.textAlign = "center"
                  shapes.forEach((shape, i) => {
                    ctx.fillStyle = shape.style.stroke
                    ctx.fillText(
                      String(shape.layerId),
                      shape.bounds.avgX * cellSize + cellSize / 2,
                      shape.bounds.avgY * cellSize + cellSize / 2,
                    )
                  })
                }
              }}
            />
          </div>
        )}
        {/* Main canvas-based renderer, with animation */}
        <div className="absolute inset-0 z-0">
          {props.renderMode === "draw" ? (
            <FlowgramDrawingRenderer
              cellSize={cellSize}
              rows={processor.rows}
              cols={processor.cols}
              lineOffset={spec.lineOffset}
              processor={processor}
              padding={settings.exportPadding * processor.cols * cellSize}
              scheme={spec.scheme}
            />
          ) : props.renderMode === "morph" ? (
            <FlowgramMorphRenderer
              cellSize={cellSize}
              rows={processor.rows}
              cols={processor.cols}
              lineOffset={spec.lineOffset}
              processor={processor}
              padding={settings.exportPadding * processor.cols * cellSize}
              scheme={spec.scheme}
            />
          ) : props.renderMode === "static" ? (
            <FlowgramSVGRenderer
              cellSize={cellSize}
              rows={processor.rows}
              cols={processor.cols}
              lineOffset={spec.lineOffset}
              padding={settings.exportPadding * processor.cols * cellSize}
              shapes={shapes}
            />
          ) : null}
        </div>
        <div
          className={["absolute inset-0 z-10 opacity-100", settings.showShapes ? "" : "opacity-0"]
            .filter(Boolean)
            .join(" ")}
        >
          <FlowgramInteractor
            cellSize={cellSize}
            rows={processor.rows}
            cols={processor.cols}
            lineOffset={spec.lineOffset}
            shapes={shapes}
            padding={settings.exportPadding}
            onClick={props.onClick}
            onDragStart={props.onDragStart}
            onDragEnd={props.onDragEnd}
            onMove={props.onMove}
            onShapeClick={props.onShapeClick}
            onShapeDown={props.onShapeDown}
            onShapeHover={props.onShapeHover}
            onShapeUp={props.onShapeUp}
          />
        </div>
      </div>
    </div>
  )
}
